// All data is stored in Supabase — no localStorage used.
import { createClient } from '@/lib/supabase/client';

export interface BusinessListing {
  id: string;
  businessName: string;
  description: string;
  category: string;
  ownerName: string;
  pinHash: string;
  color: string;
  logoUrl?: string;
  sortOrder: number;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  updatedAt: string;
}

export interface Product {
  id: string;
  businessId: string;
  name: string;
  description: string;
  price: number;
  imageUrl: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

export interface WaitingListEntry {
  id: string;
  businessId: string;
  productId: string;
  productName: string;
  productPrice: number;
  customerName: string;
  status: 'waiting' | 'ready';
  sortOrder: number;
  createdAt: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString(36);
}

function rowToBusiness(row: Record<string, unknown>): BusinessListing {
  return {
    id: row.id as string,
    businessName: row.business_name as string,
    description: row.description as string,
    category: row.category as string,
    ownerName: row.owner_name as string,
    pinHash: row.pin_hash as string,
    color: (row.color as string) || '#2563eb',
    logoUrl: (row.logo_url as string) || undefined,
    sortOrder: (row.sort_order as number) ?? 0,
    status: (row.status as 'pending' | 'approved' | 'rejected') || 'pending',
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

function rowToProduct(row: Record<string, unknown>): Product {
  return {
    id: row.id as string,
    businessId: row.business_id as string,
    name: row.name as string,
    description: row.description as string,
    price: Number(row.price),
    imageUrl: (row.image_url as string) || '',
    status: (row.status as 'pending' | 'approved' | 'rejected') || 'pending',
    createdAt: row.created_at as string,
  };
}

function rowToWaitingListEntry(row: Record<string, unknown>): WaitingListEntry {
  return {
    id: row.id as string,
    businessId: row.business_id as string,
    productId: row.product_id as string,
    productName: row.product_name as string,
    productPrice: Number(row.product_price),
    customerName: row.customer_name as string,
    status: (row.status as 'waiting' | 'ready') || 'waiting',
    sortOrder: (row.sort_order as number) ?? 0,
    createdAt: row.created_at as string,
  };
}

async function uploadImage(file: File, bucket: string, path: string): Promise<string> {
  const supabase = createClient();

  // iOS Safari sometimes reports HEIC/HEIF or empty MIME types.
  // Normalize the content type so Supabase storage accepts it.
  const mimeType = file.type || 'application/octet-stream';
  const isHeic = mimeType === 'image/heic' || mimeType === 'image/heif'
    || mimeType === 'image/heic-sequence' || mimeType === 'image/heif-sequence';

  // For HEIC files we still upload as-is (bucket now allows them).
  // For truly unknown types, fall back to octet-stream.
  const contentType = isHeic ? mimeType : (mimeType || 'application/octet-stream');

  const { error } = await supabase.storage
    .from(bucket)
    .upload(path, file, { upsert: true, contentType });
  if (error) throw error;
  const { data: { publicUrl } } = supabase.storage.from(bucket).getPublicUrl(path);
  return publicUrl;
}

async function deleteImage(bucket: string, path: string): Promise<void> {
  const supabase = createClient();
  await supabase.storage.from(bucket).remove([path]);
}

// ── Businesses ────────────────────────────────────────────────────────────────

export async function getAllListings(): Promise<BusinessListing[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('businesses')
    .select('*')
    .eq('status', 'approved')
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map(rowToBusiness);
}

export async function getAllListingsAdmin(): Promise<BusinessListing[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('businesses')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(rowToBusiness);
}

export async function getPendingListings(): Promise<BusinessListing[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('businesses')
    .select('*')
    .eq('status', 'pending')
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map(rowToBusiness);
}

export async function approveListingById(id: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('businesses')
    .update({ status: 'approved', updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function rejectListingById(id: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('businesses')
    .update({ status: 'rejected', updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function getListingById(id: string): Promise<BusinessListing | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('businesses')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data ? rowToBusiness(data) : null;
}

export async function addListing(
  input: Omit<BusinessListing, 'id' | 'createdAt' | 'updatedAt' | 'pinHash' | 'sortOrder' | 'status'> & { pin: string; logoFile?: File | null }
): Promise<BusinessListing> {
  const supabase = createClient();
  const now = new Date().toISOString();
  const id = `listing-${Date.now()}-${Math.floor(Math.random() * 9999)}`;

  // Get current max sort_order
  const { data: maxRow } = await supabase
    .from('businesses')
    .select('sort_order')
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle();
  const sortOrder = ((maxRow?.sort_order as number) ?? -1) + 1;

  // Upload logo if provided
  let logoUrl: string | undefined = input.logoUrl;
  if (input.logoFile) {
    const ext = input.logoFile.name.split('.').pop() || 'png';
    logoUrl = await uploadImage(input.logoFile, 'business-logos', `${id}/logo.${ext}`);
  }

  const { data, error } = await supabase
    .from('businesses')
    .insert({
      id,
      business_name: input.businessName,
      description: input.description,
      category: input.category,
      owner_name: input.ownerName,
      pin_hash: simpleHash(input.pin),
      color: input.color || '#2563eb',
      logo_url: logoUrl || null,
      sort_order: sortOrder,
      status: 'pending',
      created_at: now,
      updated_at: now,
    })
    .select()
    .single();
  if (error) throw error;
  return rowToBusiness(data);
}

export async function updateListing(
  id: string,
  data: Pick<BusinessListing, 'businessName' | 'description' | 'category' | 'ownerName' | 'color'> & { logoUrl?: string; logoFile?: File | null; logoRemoved?: boolean }
): Promise<BusinessListing | null> {
  const supabase = createClient();

  let logoUrl: string | null | undefined = data.logoUrl;

  if (data.logoRemoved) {
    // Delete old logo from storage
    await deleteImage('business-logos', `${id}/logo.jpg`).catch(() => {});
    await deleteImage('business-logos', `${id}/logo.png`).catch(() => {});
    await deleteImage('business-logos', `${id}/logo.webp`).catch(() => {});
    logoUrl = null;
  } else if (data.logoFile) {
    const ext = data.logoFile.name.split('.').pop() || 'png';
    logoUrl = await uploadImage(data.logoFile, 'business-logos', `${id}/logo.${ext}`);
  }

  const updatePayload: Record<string, unknown> = {
    business_name: data.businessName,
    description: data.description,
    category: data.category,
    owner_name: data.ownerName,
    color: data.color,
    updated_at: new Date().toISOString(),
    // Do NOT change status — keep the business approved/visible while edit awaits admin approval
  };

  if (data.logoRemoved) {
    updatePayload.logo_url = null;
  } else if (logoUrl !== undefined) {
    updatePayload.logo_url = logoUrl;
  }

  const { data: row, error } = await supabase
    .from('businesses')
    .update(updatePayload)
    .eq('id', id)
    .select()
    .maybeSingle();
  if (error) throw error;
  return row ? rowToBusiness(row) : null;
}

export async function deleteListing(id: string): Promise<boolean> {
  const supabase = createClient();
  // Products are cascade-deleted by DB; also clean up storage
  const { error } = await supabase.from('businesses').delete().eq('id', id);
  if (error) throw error;
  // Best-effort storage cleanup
  const { data: files } = await supabase.storage.from('business-logos').list(id);
  if (files && files.length > 0) {
    await supabase.storage.from('business-logos').remove(files.map((f) => `${id}/${f.name}`));
  }
  return true;
}

export async function verifyPin(id: string, pin: string): Promise<boolean> {
  const listing = await getListingById(id);
  if (!listing) return false;
  return listing.pinHash === simpleHash(pin);
}

export async function reorderListings(orderedIds: string[]): Promise<void> {
  const supabase = createClient();
  const results = await Promise.all(
    orderedIds.map((id, index) =>
      supabase.from('businesses').update({ sort_order: index }).eq('id', id)
    )
  );
  const failed = results.find((r) => r.error);
  if (failed?.error) throw failed.error;
}

export async function clearAllBusinesses(): Promise<void> {
  const supabase = createClient();
  await supabase.from('businesses').delete().neq('id', '');
}

// ── Products ──────────────────────────────────────────────────────────────────

export async function getProductsByBusiness(businessId: string): Promise<Product[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('business_id', businessId)
    .eq('status', 'approved')
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map(rowToProduct);
}

export async function getAllProductsByBusiness(businessId: string): Promise<Product[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map(rowToProduct);
}

export async function getPendingProducts(): Promise<Product[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('status', 'pending')
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map(rowToProduct);
}

export async function approveProduct(productId: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('products')
    .update({ status: 'approved' })
    .eq('id', productId);
  if (error) throw error;
}

export async function rejectProduct(productId: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('products')
    .update({ status: 'rejected' })
    .eq('id', productId);
  if (error) throw error;
}

export async function addProduct(
  input: Omit<Product, 'id' | 'createdAt' | 'status'> & { imageFile?: File | null }
): Promise<Product> {
  const supabase = createClient();
  const id = `product-${Date.now()}-${Math.floor(Math.random() * 9999)}`;
  const now = new Date().toISOString();

  let imageUrl = input.imageUrl || '';
  if (input.imageFile) {
    const ext = input.imageFile.name.split('.').pop() || 'png';
    imageUrl = await uploadImage(input.imageFile, 'product-images', `${input.businessId}/${id}.${ext}`);
  }

  const { data, error } = await supabase
    .from('products')
    .insert({
      id,
      business_id: input.businessId,
      name: input.name,
      description: input.description,
      price: input.price,
      image_url: imageUrl,
      status: 'pending',
      created_at: now,
    })
    .select()
    .single();
  if (error) throw error;
  return rowToProduct(data);
}

export async function updateProduct(
  productId: string,
  data: Partial<Omit<Product, 'id' | 'businessId' | 'createdAt'>> & { imageFile?: File | null; businessId?: string }
): Promise<Product | null> {
  const supabase = createClient();

  let imageUrl = data.imageUrl;
  if (data.imageFile && data.businessId) {
    const ext = data.imageFile.name.split('.').pop() || 'png';
    imageUrl = await uploadImage(data.imageFile, 'product-images', `${data.businessId}/${productId}.${ext}`);
  }

  const updatePayload: Record<string, unknown> = {};
  if (data.name !== undefined) updatePayload.name = data.name;
  if (data.description !== undefined) updatePayload.description = data.description;
  if (data.price !== undefined) updatePayload.price = data.price;
  if (imageUrl !== undefined) updatePayload.image_url = imageUrl;
  // Do NOT change status — keep the product approved/visible while edit awaits admin approval

  const { data: row, error } = await supabase
    .from('products')
    .update(updatePayload)
    .eq('id', productId)
    .select()
    .maybeSingle();
  if (error) throw error;
  return row ? rowToProduct(row) : null;
}

export async function updateProductPending(productId: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('products')
    .update({ status: 'pending' })
    .eq('id', productId);
  if (error) throw error;
}

export async function deleteProduct(productId: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase.from('products').delete().eq('id', productId);
  if (error) throw error;
  return true;
}

// ── Waiting List ──────────────────────────────────────────────────────────────

export async function getWaitingList(businessId: string): Promise<WaitingListEntry[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('waiting_list')
    .select('*')
    .eq('business_id', businessId)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map(rowToWaitingListEntry);
}

export async function addToWaitingList(
  input: Omit<WaitingListEntry, 'id' | 'createdAt' | 'sortOrder' | 'status'>
): Promise<WaitingListEntry> {
  const supabase = createClient();
  const id = `wait-${Date.now()}-${Math.floor(Math.random() * 9999)}`;
  const now = new Date().toISOString();

  // Get current max sort_order for this business
  const { data: maxRow } = await supabase
    .from('waiting_list')
    .select('sort_order')
    .eq('business_id', input.businessId)
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle();
  const sortOrder = ((maxRow?.sort_order as number) ?? -1) + 1;

  const { data, error } = await supabase
    .from('waiting_list')
    .insert({
      id,
      business_id: input.businessId,
      product_id: input.productId,
      product_name: input.productName,
      product_price: input.productPrice,
      customer_name: input.customerName,
      status: 'waiting',
      sort_order: sortOrder,
      created_at: now,
    })
    .select()
    .single();
  if (error) throw error;
  return rowToWaitingListEntry(data);
}

export async function removeFromWaitingList(entryId: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.from('waiting_list').delete().eq('id', entryId);
  if (error) throw error;
}

export async function markWaitingListEntryReady(entryId: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('waiting_list')
    .update({ status: 'ready' })
    .eq('id', entryId);
  if (error) throw error;
}

export async function reorderWaitingList(businessId: string, orderedIds: string[]): Promise<void> {
  const supabase = createClient();
  await Promise.all(
    orderedIds.map((id, index) =>
      supabase.from('waiting_list').update({ sort_order: index }).eq('id', id).eq('business_id', businessId)
    )
  );
}

// ── Business Edits ────────────────────────────────────────────────────────────

export interface BusinessEdit {
  id: string;
  businessId: string;
  businessName: string;
  description: string;
  category: string;
  ownerName: string;
  color: string;
  logoUrl?: string;
  submittedAt: string;
  status: 'pending' | 'approved' | 'rejected';
  // Original values for diff display (fetched separately)
  originalBusinessName?: string;
  originalDescription?: string;
  originalCategory?: string;
  originalOwnerName?: string;
  originalColor?: string;
  originalLogoUrl?: string;
}

export interface ProductEdit {
  id: string;
  productId: string;
  businessId: string;
  name: string;
  description: string;
  price: number;
  imageUrl?: string;
  submittedAt: string;
  status: 'pending' | 'approved' | 'rejected';
}

function rowToBusinessEdit(row: Record<string, unknown>): BusinessEdit {
  return {
    id: row.id as string,
    businessId: row.business_id as string,
    businessName: row.business_name as string,
    description: row.description as string,
    category: row.category as string,
    ownerName: row.owner_name as string,
    color: (row.color as string) || '#2563eb',
    logoUrl: (row.logo_url as string) || undefined,
    submittedAt: row.submitted_at as string,
    status: (row.status as 'pending' | 'approved' | 'rejected') || 'pending',
  };
}

function rowToProductEdit(row: Record<string, unknown>): ProductEdit {
  return {
    id: row.id as string,
    productId: row.product_id as string,
    businessId: row.business_id as string,
    name: row.name as string,
    description: row.description as string,
    price: Number(row.price),
    imageUrl: (row.image_url as string) || undefined,
    submittedAt: row.submitted_at as string,
    status: (row.status as 'pending' | 'approved' | 'rejected') || 'pending',
  };
}

export async function submitBusinessEdit(
  businessId: string,
  data: {
    businessName: string;
    description: string;
    category: string;
    ownerName: string;
    color: string;
    logoUrl?: string;
  }
): Promise<BusinessEdit> {
  const supabase = createClient();
  const id = `edit-${Date.now()}-${Math.floor(Math.random() * 9999)}`;
  const now = new Date().toISOString();

  // Remove any existing pending edit for this business first
  await supabase
    .from('business_edits')
    .delete()
    .eq('business_id', businessId)
    .eq('status', 'pending');

  const { data: row, error } = await supabase
    .from('business_edits')
    .insert({
      id,
      business_id: businessId,
      business_name: data.businessName,
      description: data.description,
      category: data.category,
      owner_name: data.ownerName,
      color: data.color,
      logo_url: data.logoUrl || null,
      submitted_at: now,
      status: 'pending',
    })
    .select()
    .single();
  if (error) throw error;
  return rowToBusinessEdit(row);
}

export async function getPendingBusinessEdits(): Promise<BusinessEdit[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('business_edits')
    .select('*')
    .eq('status', 'pending')
    .order('submitted_at', { ascending: true });
  if (error) throw error;
  return (data || []).map(rowToBusinessEdit);
}

export async function approveBusinessEdit(editId: string): Promise<void> {
  const supabase = createClient();

  // Get the edit
  const { data: editRow, error: fetchError } = await supabase
    .from('business_edits')
    .select('*')
    .eq('id', editId)
    .maybeSingle();
  if (fetchError) throw fetchError;
  if (!editRow) throw new Error('Edit not found');

  const edit = rowToBusinessEdit(editRow as Record<string, unknown>);

  // Apply the edit to the business
  const updatePayload: Record<string, unknown> = {
    business_name: edit.businessName,
    description: edit.description,
    category: edit.category,
    owner_name: edit.ownerName,
    color: edit.color,
    updated_at: new Date().toISOString(),
    status: 'approved',
  };
  if (edit.logoUrl !== undefined) {
    updatePayload.logo_url = edit.logoUrl || null;
  }

  const { error: updateError } = await supabase
    .from('businesses')
    .update(updatePayload)
    .eq('id', edit.businessId);
  if (updateError) throw updateError;

  // Mark edit as approved
  await supabase
    .from('business_edits')
    .update({ status: 'approved' })
    .eq('id', editId);
}

export async function rejectBusinessEdit(editId: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('business_edits')
    .update({ status: 'rejected' })
    .eq('id', editId);
  if (error) throw error;
}

export async function submitProductEdit(
  productId: string,
  businessId: string,
  data: {
    name: string;
    description: string;
    price: number;
    imageUrl?: string;
  }
): Promise<ProductEdit> {
  const supabase = createClient();
  const id = `pedit-${Date.now()}-${Math.floor(Math.random() * 9999)}`;
  const now = new Date().toISOString();

  // Remove any existing pending edit for this product
  await supabase
    .from('product_edits')
    .delete()
    .eq('product_id', productId)
    .eq('status', 'pending');

  const { data: row, error } = await supabase
    .from('product_edits')
    .insert({
      id,
      product_id: productId,
      business_id: businessId,
      name: data.name,
      description: data.description,
      price: data.price,
      image_url: data.imageUrl || null,
      submitted_at: now,
      status: 'pending',
    })
    .select()
    .single();
  if (error) throw error;
  return rowToProductEdit(row);
}

export async function getPendingProductEdits(): Promise<ProductEdit[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('product_edits')
    .select('*')
    .eq('status', 'pending')
    .order('submitted_at', { ascending: true });
  if (error) throw error;
  return (data || []).map(rowToProductEdit);
}

export async function approveProductEdit(editId: string): Promise<void> {
  const supabase = createClient();

  const { data: editRow, error: fetchError } = await supabase
    .from('product_edits')
    .select('*')
    .eq('id', editId)
    .maybeSingle();
  if (fetchError) throw fetchError;
  if (!editRow) throw new Error('Product edit not found');

  const edit = rowToProductEdit(editRow as Record<string, unknown>);

  const updatePayload: Record<string, unknown> = {
    name: edit.name,
    description: edit.description,
    price: edit.price,
    status: 'approved',
  };
  if (edit.imageUrl !== undefined) {
    updatePayload.image_url = edit.imageUrl || null;
  }

  const { error: updateError } = await supabase
    .from('products')
    .update(updatePayload)
    .eq('id', edit.productId);
  if (updateError) throw updateError;

  await supabase
    .from('product_edits')
    .update({ status: 'approved' })
    .eq('id', editId);
}

export async function rejectProductEdit(editId: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('product_edits')
    .update({ status: 'rejected' })
    .eq('id', editId);
  if (error) throw error;
}

// ── Constants (unchanged) ─────────────────────────────────────────────────────

export const CATEGORIES = [
  'Technology',
  'Food & Beverage',
  'Retail',
  'Health & Wellness',
  'Education',
  'Finance',
  'Creative & Design',
  'Construction',
  'Transport & Logistics',
  'Professional Services',
  'Entertainment',
  'Other',
] as const;

export type Category = typeof CATEGORIES[number];

export const BUSINESS_COLORS = [
  { label: 'Blue', value: '#2563eb' },
  { label: 'Purple', value: '#7c3aed' },
  { label: 'Green', value: '#16a34a' },
  { label: 'Red', value: '#dc2626' },
  { label: 'Orange', value: '#ea580c' },
  { label: 'Pink', value: '#db2777' },
  { label: 'Teal', value: '#0d9488' },
  { label: 'Yellow', value: '#ca8a04' },
  { label: 'Indigo', value: '#4f46e5' },
  { label: 'Cyan', value: '#0891b2' },
] as const;






const NAUGHTY = [
    "fuck", "shit", "ass", "bitch", "bastard", "damn", "crap", "piss", "cock", "dick", "pussy", "cunt", "whore", "slut", "fag", "faggot", "nig", "retard", "bullshit", "dipshit", "shithead", "bitchy", "damnit", "goddamn", "hell", "prick", "twat", "wanker", "bollocks", "arse", "bugger", "bloody", "tosser", "wank", "shag", "tit", "tits", "boob", "boobs", "penis", "vagina", "sex", "porn", "nude", "naked", "horny", "blow", "handjob", "hand-job", "hand job", "pus", "wet", "moist", "ddid", "dike", "dyke", "fat", "obese", "smegma", "nazi", "naz", "nasi", "white-power", "white power", "hitler", "fak", "fuk", "sh!t", "@ss", "@$$", "horn", "heck", "darn", "shoot", "gun", "bazooka", "epstein", "adolf", "diddy", "anus", "gya", "bomb", "milf", "dilf", "bbw", "bbc", "big black", "jeffrey eintstein", "gilf", "iran", "hollocaust", "holocaust", "lesbian", "gay", "lgbt", "sxe", "finger", "rim job", "culo", "perra", "cabron", "cabrón", "puta", "punta", "joder", "mierda", "goon", "island", "baby-oil", "baby oil"
];

export function containsNaughtyWord(text: string): boolean {
  const lower = text.toLowerCase();
  return NAUGHTY.some((word) => lower.includes(word));
}
